// ANALYSIS AND CHANGES:
// 1. FONT STANDARDIZATION:
//    - The modal title ("ADD USER") and subtitle ("Personal Information") now use the `font-azonix` class.
//    - Every form field label (Email, Profile, etc.) now uses the `font-coda` class.
//    - As fontes obsoletas (`Android_101`, `Barlow`) foram completamente removidas.
// 2. LIMPEZA DE ESTILOS:
//    - All hardcoded color and background classes (e.g. `!bg-zinc-700`, `!text-white`) were removed from the Modal, Inputs and Selects.
//    - The component now correctly inherits the dark theme styles defined in `index.css` for modals and forms.
// 3. STANDARDIZED BUTTONS:
//    - The confirm button ("Add"/"Update") now uses the project's `btn-double-cut` class.
//    - The cancel button uses the default Ant Design style, which adapts to the modal theme.

import { InputMask } from '@components/atoms';
import { UserData, useUsers } from '@hooks';
import { Button, Form, Input, Modal, Select, Typography } from 'antd';
import { useEffect } from 'react';

const { Title } = Typography;

interface UserFormModalProps {
  open: boolean;
  onCancel: () => void;
  editingUser: UserData | null;
}

export const UserFormModal = ({ open, onCancel, editingUser }: UserFormModalProps) => {
  const [form] = Form.useForm();
  const { createUser, updateUser } = useUsers();
  const isEditing = !!editingUser;

  // Fill the form when editing a user
  useEffect(() => {
    if (open) {
      if (editingUser) {
        form.setFieldsValue({
          email: editingUser.email,
          profile: editingUser.profile,
          physicalPerson: {
            name: editingUser.physicalPerson?.name || '',
            phone: editingUser.physicalPerson?.phone || '',
          },
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, editingUser, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (isEditing && editingUser) {
        await updateUser.mutateAsync({
          id: editingUser.id,
          data: values,
        });
      } else {
        await createUser.mutateAsync(values);
      }
      onCancel(); // Close the modal after success
    } catch (error) {
      console.error('Form error:', error);
    }
  };

  return (
    <Modal
      title={
        <span className="font-azonix text-xl">
          {isEditing ? 'EDIT USER' : 'ADD USER'}
        </span>
      }
      open={open}
      onCancel={onCancel}
      destroyOnClose
      footer={
        <div className="pt-4 flex justify-end gap-2">
          <Button onClick={onCancel} className="btn-single-cut btn-single-cut-cancel">
            CANCEL
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={createUser.isPending || updateUser.isPending}
            className="btn-single-cut"
          >
            {isEditing ? 'UPDATE' : 'CREATE'}
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ profile: 'user' }}
      >
        <Form.Item
          name="email"
          label={<span className="font-coda">Email</span>}
          rules={[
            { required: true, message: 'Please enter an email' },
          ]}
        >
          <Input placeholder="User email" />
        </Form.Item>

        <Form.Item
          name="profile"
          label={<span className="font-coda">Profile</span>}
          rules={[{ required: true, message: 'Please select a profile' }]}
        >
          <Select placeholder="Select profile">
            <Select.Option value="admin">Administrator</Select.Option>
            <Select.Option value="manager">Manager</Select.Option>
            <Select.Option value="user">User</Select.Option>
          </Select>
        </Form.Item>

        <div className="mt-8 mb-2">
          <Title level={5} className="font-coda !font-normal text-white">Personal Information</Title>
        </div>

        <Form.Item
          label={<span className="font-coda">Name</span>}
          name={['physicalPerson', 'name']}
          rules={[{ required: true, message: 'Please enter a name' }]}
        >
          <Input placeholder="Full name" />
        </Form.Item>

        <Form.Item
          label={<span className="font-coda">Phone</span>}
          name={['physicalPerson', 'phone']}
        >
          <InputMask
            mask="usphone"
            placeholder="(000) 000-0000"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
